import { useState, useEffect } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, Stack, FormControl, FormLabel, Input, Textarea, Button, Switch, Typography, Box, Chip, ModalOverflow } from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';



export default function CategoryModal({ open, category, parentCategory, onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        is_active: true
    });
    const [loading, setLoading] = useState(false);

    // Сбрасываем форму при открытии/закрытии модального окна
    useEffect(() => {
        if (open) {
            if (category) {
                // Режим редактирования
                setFormData({
                    name: category.name || '',
                    description: category.description || '',
                    is_active: category.is_active !== undefined ? category.is_active : true
                });
            } else {
                // Режим создания
                setFormData({
                    name: '',
                    description: '',
                    is_active: true
                });
            }
        }
    }, [open, category]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await onSave(formData);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const isEditing = !!category;

    return (
        <AnimatePresence>
            {open && (
                <Modal 
                    open={open} 
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto', maxHeight: '90vh' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            sx={{
                                width: '90%',
                                maxWidth: 500,
                                maxHeight: '90vh',
                                overflow: 'auto'
                            }}
                        >
                            <ModalClose />
                            
                            <DialogTitle>
                                {isEditing ? 'Редактирование категории' : 'Создание категории'}
                            </DialogTitle>

                            <DialogContent>
                                <Stack spacing={3} component="form" onSubmit={handleSubmit}>
                                    {/* Информация о родительской категории */}
                                    {parentCategory && !isEditing && (
                                        <Box>
                                            <Typography level="body2" color="neutral" sx={{ mb: 1 }}>
                                                Родительская категория:
                                            </Typography>
                                            <Chip variant="soft" color="primary">
                                                {parentCategory.name}
                                            </Chip>
                                        </Box>
                                    )}

                                    {/* Название категории */}
                                    <FormControl required>
                                        <FormLabel>Название категории</FormLabel>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            placeholder="Введите название категории"
                                            disabled={loading}
                                        />
                                    </FormControl>

                                    {/* Описание категории */}
                                    <FormControl>
                                        <FormLabel>Описание категории</FormLabel>
                                        <Textarea
                                            value={formData.description}
                                            onChange={(e) => handleChange('description', e.target.value)}
                                            placeholder="Введите описание категории (необязательно)"
                                            minRows={3}
                                            disabled={loading}
                                        />
                                    </FormControl>

                                    {/* Активность категории */}
                                    <FormControl>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Switch
                                                checked={formData.is_active}
                                                onChange={(e) => handleChange('is_active', e.target.checked)}
                                                disabled={loading}
                                            />
                                            <Box>
                                                <FormLabel>Активная категория</FormLabel>
                                                <Typography level="body2" color="neutral">
                                                    {formData.is_active 
                                                        ? 'Категория будет отображаться пользователям' 
                                                        : 'Категория будет скрыта от пользователей'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </FormControl>

                                    {/* Кнопки */}
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            loading={loading}
                                            disabled={!formData.name.trim()}
                                        >
                                            {isEditing ? 'Сохранить' : 'Создать'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}